import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

// context
import LayoutProvider from '../../context/LayoutProvider/LayoutProvider';

// components
import { AppRoot } from '../../components';


const SITE_URL = 'https://www.weathersafe.co.uk';
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;
const BRAND_SUFFIX = ' | Weathersafe';


// Convert a URL slug to a human-readable breadcrumb label.
// e.g. "about-gap-77" → "About GAP77"
function slugToLabel(slug) {
  const special = { gap: 'GAP', eudr: 'EUDR', ai: 'AI', ml: 'ML', cws: 'CWS' };
  return slug
    .split('-')
    .map(part => {
      const lower = part.toLowerCase();
      if (special[lower]) return special[lower];
      if (/^\d+$/.test(part)) return part; // keep pure numbers
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ')
    .replace(/Gap77/g, 'GAP77');
}


function buildBreadcrumbSchema(pathname) {
  if (!pathname || pathname === '/') return null;
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
  ];
  let accum = '';
  parts.forEach((slug, idx) => {
    accum += `/${slug}`;
    items.push({
      '@type': 'ListItem',
      position: idx + 2,
      name: slugToLabel(slug),
      item: `${SITE_URL}${accum}`,
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}


function AppLayout({
  footerVariant,
  layoutClass = "1",
  title = 'page title goes here',
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  noindex = false,
  jsonLd,
  children,
}) {
  const location = useLocation();

  const fullTitle = title.includes('Weathersafe') ? title : `${title}${BRAND_SUFFIX}`;
  const canonicalUrl = canonical || `${SITE_URL}${location.pathname}`;
  const resolvedOgTitle = ogTitle || fullTitle;
  const resolvedOgDescription = ogDescription || description;
  const resolvedOgImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`)
    : DEFAULT_OG_IMAGE;

  // Collect all JSON-LD schemas to render
  const schemas = [];
  if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
  else if (jsonLd) schemas.push(jsonLd);

  // Auto-add BreadcrumbList for non-landing, indexable pages (unless page already provides one)
  const hasBreadcrumbs = schemas.some(s => s && s['@type'] === 'BreadcrumbList');
  if (!noindex && !hasBreadcrumbs) {
    const crumb = buildBreadcrumbSchema(location.pathname);
    if (crumb) schemas.push(crumb);
  }

  return (
    <LayoutProvider>
      <Helmet>
        <title>{fullTitle}</title>
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonicalUrl} />
        {noindex && <meta name="robots" content="noindex, nofollow" />}

        {/* Open Graph */}
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content="Weathersafe" />
        <meta property="og:title" content={resolvedOgTitle} />
        {resolvedOgDescription && <meta property="og:description" content={resolvedOgDescription} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={resolvedOgImage} />
        <meta property="og:locale" content="en_GB" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={resolvedOgTitle} />
        {resolvedOgDescription && <meta name="twitter:description" content={resolvedOgDescription} />}
        <meta name="twitter:image" content={resolvedOgImage} />

        {/* JSON-LD structured data */}
        {schemas.map((schema, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      <AppRoot footerVariant={footerVariant} layoutClass={layoutClass}>
        {children}
      </AppRoot>
    </LayoutProvider>
  )
}

export default AppLayout;
